import PropTypes from 'prop-types';

import {Box, Flex, Heading, Skeleton, Text} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component} from '@amblique/king-living/app/components/contentful/component';

/**
 * Hero Banner Block (CF-04)
 * Full-viewport hero with background image/video, title overlay, and bottom action bar
 *
 * @typedef {import('@chakra-ui/styled-system').SystemStyleObject} SystemStyleObject
 * @typedef {"wrapper" | "backgroundWrapper" | "contentWrapper" | "title" | "subtitle" | "bottomBar" | "leftCta" | "rightCta"} HeroBannerBlockCF04Parts
 * @typedef {Partial<Record<HeroBannerBlockCF04Parts, SystemStyleObject>>} HeroBannerBlockCF04Styles
 *
 * @param {Object} props - Component props
 * @param {Contentful.HeroBanner} props.component - HeroBanner component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {HeroBannerBlockCF04Styles} [props.styles] - Component styles
 * @returns {React.ReactElement}
 */
export const HeroBannerBlockCF04 = ({component, isComplete = true, isFetching = false, styles, ...props}) => {
  const {backgroundMedia, headingTitle, subtitle, cta, rightCta} = component;

  // Determine which background to use (Image has priority over Video)
  const backgroundImage = backgroundMedia?.__typename === 'Image' ? backgroundMedia : null;
  const backgroundVideo = backgroundMedia?.__typename === 'Video' ? backgroundMedia : null;

  // const hasRightCta = rightCta && (Array.isArray(rightCta) ? rightCta.length > 0 : true);

  return (
    <Box {...styles.wrapper}>
      {/* Background Media Layer */}
      <Box className="hero-image-wrapper" {...styles.backgroundWrapper}>
        {backgroundImage ? (
          <Component component={backgroundImage} {...styles.backgroundMedia} />
        ) : backgroundVideo ? (
          <Component component={backgroundVideo} {...styles.backgroundMedia} />
        ) : (
          <Skeleton height="100%" width="100%" />
        )}
      </Box>

      {/* Content Overlay Layer */}
      <Box className="hero-content-wrapper" {...styles.contentWrapper}>
        {!isComplete && isFetching ? (
          <Skeleton height="120px" />
        ) : (
          <>
            {headingTitle && <Heading {...styles.title}>{headingTitle}</Heading>}
            {subtitle && <Text {...styles.subtitle}>{subtitle}</Text>}
          </>
        )}

        {/* Bottom Action Bar */}
        {(cta || rightCta) && (
          <Flex {...styles.bottomBar}>
            {/* Left CTA */}
            {cta && (
              <Box {...styles.leftCta}>
                <Component component={cta} />
              </Box>
            )}

            {/* Right CTA - only shown if rightCta is configured */}
            {rightCta && (
              <Box {...styles.rightCta}>
                <Component component={rightCta} />
              </Box>
            )}
          </Flex>
        )}
      </Box>
    </Box>
  );
};

HeroBannerBlockCF04.displayName = 'HeroBannerBlockCF04';
HeroBannerBlockCF04.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  styles: PropTypes.object.isRequired,
};
